import { number, object } from 'yup';
import {
  DeferredCompensationContributionUnit,
  DeferredCompensationDTO,
  DeferredCompensationOptionsDTO,
} from '../../tables/DeferredCompensation/DeferredCompensation.meta';
import { OptionType } from '../../../../CommonComponents/OPL/Select/BaseSelect/types';

export const PRINCIPAL_ID = '00000000-0000-0000-0000-000000000000';

const currentYear = new Date().getFullYear();

interface FormContext {
  options: DeferredCompensationOptionsDTO;
}

export interface SaveDeferredCompensationDTO
  extends Omit<DeferredCompensationDTO, 'Id' | 'Name' | 'YearlyContribution' | 'OtherAssetType'> {
  Id: string | null;
  Name: string | null;
  YearlyContribution: number | null;
  ClientId: string;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean;
}

export interface DeferredCompensationFormValues {
  id: string | null;
  name: string | null;
  earner: string;
  value: number | null;
  yearlyContribution: number | null;
  contributionUnit: DeferredCompensationContributionUnit;
  contributionStartAge: number;
  contributionEndYear: number | null;
  distributionStartAge: number | null;
  distributionPeriod: number;
  accumulationGrowthRate: number;
  distributionGrowthRate: number;
  customBucketIndex: number | null;
  isLiquid: boolean;
  includeInGrossEstate: boolean;
  orionManagementStyleId: number | null;
}

export const DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS: OptionType<
  DeferredCompensationContributionUnit
>[] = [
  { label: '$', value: DeferredCompensationContributionUnit.Dollar },
  { label: '%', value: DeferredCompensationContributionUnit.Percentage },
];

export const DEFERRED_COMPENSATION_FORM_SCHEMA = object({
  value: number().typeError('Enter the current value of this asset'),
  yearlyContribution: number()
    .transform((value, originalValue) => (originalValue === null ? null : value))
    .nullable()
    .when('contributionUnit', {
      is: DeferredCompensationContributionUnit.Percentage,
      then: schema => schema.max(100, 'The annual contribution must be less or equal to 100%'),
    }),
  accumulationGrowthRate: number().typeError('Enter the accumulation growth rate'),
  distributionGrowthRate: number().typeError('Enter the distribution growth rate'),
  distributionStartAge: number()
    .nullable()
    .test(
      'lessThanContributionEndYear',
      'Distribution must occur after contribution end year',
      // eslint-disable-next-line func-names
      function(value) {
        const isAtRetirement = value === null;

        const { contributionEndYear, earner } = this.parent;

        const { options } = this.options.context as FormContext;

        const isEarnerPrincipal = earner === PRINCIPAL_ID;

        const earnerAge = isEarnerPrincipal ? options.principalAge : options.spouseAge;

        const earnerRetirementAge = isEarnerPrincipal
          ? options.principalRetirementAge
          : options.spouseRetirementAge;

        const isContributionEndYearRetirement = contributionEndYear === null;

        const contributionEndAge = isContributionEndYearRetirement
          ? earnerRetirementAge
          : contributionEndYear - currentYear + earnerAge;

        if (isContributionEndYearRetirement && isAtRetirement) {
          return true;
        }

        if (isAtRetirement) {
          return earnerRetirementAge > contributionEndAge;
        }

        if (value) {
          return value > contributionEndAge;
        }
        return true;
      },
    ),
});
